/**
 * Relay Mutation Wrapper: GetGlobalUnpaidInvoiceSummary
 * 
 * PURPOSE:
 * - Fetches deduplicated global summary of unpaid invoices for banner display
 * - Solves overcount problem where invoices duplicated across orgs inflate totals
 * - Each invoice counted only once (by flexiId) regardless of how many orgs share it
 * 
 * USAGE:
 * - Called from OverdueBillsList when switching to Organizations View
 * - Result displayed in "Total unpaid" banner at top of page
 * - Separate from table data (which shows per-org breakdown from getOrganizationsWithUnpaidInvoices)
 * 
 * RELAY PATTERN:
 * - Uses REQUIRED_CHILDREN config to fetch mutation result
 * - No optimistic updates needed (data loads quickly)
 * - No store updates needed (banner uses state, not Relay store)
 * 
 * @module GetGlobalUnpaidInvoiceSummary
 */

import Relay from 'react-relay';

export default class GetGlobalUnpaidInvoiceSummary extends Relay.Mutation {
    /**
     * Specify the GraphQL mutation to execute
     */
    getMutation() {
        return Relay.QL`mutation { getGlobalUnpaidInvoiceSummary }`;
    }

    /**
     * Define all fields that might change (fat query)
     * Relay will intersect this with tracked queries
     */
    getFatQuery() {
        return Relay.QL`
            fragment on GetGlobalUnpaidInvoiceSummaryPayload {
                totalCZK
                totalEUR
                totalOther
                otherCurrency
                totalOverdueCZK
                totalOverdueEUR
                totalOverdueOther
                totalOverdueUp30CZK
                totalOverdue31to60CZK
                totalOverdue61to90CZK
                totalOverdueOver90CZK
                totalOverdueUp30EUR
                totalOverdue31to60EUR
                totalOverdue61to90EUR
                totalOverdueOver90EUR
                totalOverdueUp30Other
                totalOverdue31to60Other
                totalOverdue61to90Other
                totalOverdueOver90Other
                totalNotOverdueCZK
                totalNotOverdueEUR
                totalNotOverdueOther
                totalBlockedCZK
                totalBlockedEUR
                totalBlockedOther
                totalNotBlockedCZK
                totalNotBlockedEUR
                totalNotBlockedOther
                totalBlockedOverdueCZK
                totalBlockedOverdueEUR
                totalBlockedOverdueOther
                totalBlockedOverdueUp30CZK
                totalBlockedOverdue31to60CZK
                totalBlockedOverdue61to90CZK
                totalBlockedOverdueOver90CZK
                totalBlockedOverdueUp30EUR
                totalBlockedOverdue31to60EUR
                totalBlockedOverdue61to90EUR
                totalBlockedOverdueOver90EUR
                totalBlockedOverdueUp30Other
                totalBlockedOverdue31to60Other
                totalBlockedOverdue61to90Other
                totalBlockedOverdueOver90Other
                totalBlockedNotOverdueCZK
                totalBlockedNotOverdueEUR
                totalBlockedNotOverdueOther
                totalNotBlockedOverdueCZK
                totalNotBlockedOverdueEUR
                totalNotBlockedOverdueOther
                totalNotBlockedOverdueUp30CZK
                totalNotBlockedOverdue31to60CZK
                totalNotBlockedOverdue61to90CZK
                totalNotBlockedOverdueOver90CZK
                totalNotBlockedOverdueUp30EUR
                totalNotBlockedOverdue31to60EUR
                totalNotBlockedOverdue61to90EUR
                totalNotBlockedOverdueOver90EUR
                totalNotBlockedOverdueUp30Other
                totalNotBlockedOverdue31to60Other
                totalNotBlockedOverdue61to90Other
                totalNotBlockedOverdueOver90Other
                totalNotBlockedNotOverdueCZK
                totalNotBlockedNotOverdueEUR
                totalNotBlockedNotOverdueOther
            }
        `;
    }

    /**
     * Configure how to handle mutation result
     * REQUIRED_CHILDREN: Forces Relay to fetch specified fields and pass to callback
     */
    getConfigs() {
        return [{
            type: 'REQUIRED_CHILDREN',
            children: [
                Relay.QL`
                    fragment on GetGlobalUnpaidInvoiceSummaryPayload {
                        totalCZK
                        totalEUR
                        totalOther
                        otherCurrency
                        totalOverdueCZK
                        totalOverdueEUR
                        totalOverdueOther
                        totalOverdueUp30CZK
                        totalOverdue31to60CZK
                        totalOverdue61to90CZK
                        totalOverdueOver90CZK
                        totalOverdueUp30EUR
                        totalOverdue31to60EUR
                        totalOverdue61to90EUR
                        totalOverdueOver90EUR
                        totalOverdueUp30Other
                        totalOverdue31to60Other
                        totalOverdue61to90Other
                        totalOverdueOver90Other
                        totalNotOverdueCZK
                        totalNotOverdueEUR
                        totalNotOverdueOther
                        totalBlockedCZK
                        totalBlockedEUR
                        totalBlockedOther
                        totalNotBlockedCZK
                        totalNotBlockedEUR
                        totalNotBlockedOther
                        totalBlockedOverdueCZK
                        totalBlockedOverdueEUR
                        totalBlockedOverdueOther
                        totalBlockedOverdueUp30CZK
                        totalBlockedOverdue31to60CZK
                        totalBlockedOverdue61to90CZK
                        totalBlockedOverdueOver90CZK
                        totalBlockedOverdueUp30EUR
                        totalBlockedOverdue31to60EUR
                        totalBlockedOverdue61to90EUR
                        totalBlockedOverdueOver90EUR
                        totalBlockedOverdueUp30Other
                        totalBlockedOverdue31to60Other
                        totalBlockedOverdue61to90Other
                        totalBlockedOverdueOver90Other
                        totalBlockedNotOverdueCZK
                        totalBlockedNotOverdueEUR
                        totalBlockedNotOverdueOther
                        totalNotBlockedOverdueCZK
                        totalNotBlockedOverdueEUR
                        totalNotBlockedOverdueOther
                        totalNotBlockedOverdueUp30CZK
                        totalNotBlockedOverdue31to60CZK
                        totalNotBlockedOverdue61to90CZK
                        totalNotBlockedOverdueOver90CZK
                        totalNotBlockedOverdueUp30EUR
                        totalNotBlockedOverdue31to60EUR
                        totalNotBlockedOverdue61to90EUR
                        totalNotBlockedOverdueOver90EUR
                        totalNotBlockedOverdueUp30Other
                        totalNotBlockedOverdue31to60Other
                        totalNotBlockedOverdue61to90Other
                        totalNotBlockedOverdueOver90Other
                        totalNotBlockedNotOverdueCZK
                        totalNotBlockedNotOverdueEUR
                        totalNotBlockedNotOverdueOther
                    }
                `
            ]
        }];
    }

    /**
     * Variables to pass to mutation
     * No input required - backend fetches all unpaid invoices globally
     */
    getVariables() {
        return {};
    }
}
